import { P, RP, stepLeq } from "apparatus/library/common";
import { Adjustable, Interactable } from "editing/interaction";
import { Group, Key, Path, PointText } from "paper";
import * as React from "react";
import store, { appStore } from "store/store";
const MIN_EDGE_SIZE = 100;
const COLOR = "#d22209";
const ALMOST_TRANSPARENT = "#ffffff01";
const VISIBLE_STROKE_WIDTH = 4.0;
const HITTABLE_STROKE_WIDTH = 14.0;
const FONT_SIZE = 16.0;
export class RenderAreaBox extends React.Component {
    constructor(props) {
        super(props);
        this.state = { left: props.left, bottom: props.bottom, right: props.right, top: props.top };
        this.moveAllAdjustableComponent = new EdgeResizeComponent(this, ["top", "right", "left", "bottom"]);
        this.visibleRect = new (Interactable(Path, Adjustable(this.moveAllAdjustableComponent)))([[0, 0], [1, 0], [1, 1], [0, 1]])
            .close().withStroke(VISIBLE_STROKE_WIDTH, COLOR);
        this.visibleRect.strokeCap = "square";
        this.text = new (Interactable(PointText, Adjustable(this.moveAllAdjustableComponent)))({
            point: P(0, 0),
            content: "0x0",
            fillColor: COLOR,
            fontFamily: 'Rubik',
            fontWeight: 'bold',
            fontSize: FONT_SIZE,
            justification: 'left'
        });
        this.addCursor(this.text, "move");
        this.text.visible = false;
        this.group = new Group([this.visibleRect, this.text]);
        // Add transparent items.
        this.movableRect = new (Interactable(Path, Adjustable(this.moveAllAdjustableComponent)))([[0, 0], [1, 0], [1, 1], [0, 1]])
            .withStroke(HITTABLE_STROKE_WIDTH, ALMOST_TRANSPARENT).close();
        this.addCursor(this.movableRect, "move");
        this.cornerTopLeft = new (Interactable(Path.Circle, Adjustable(new EdgeResizeComponent(this, ["top", "left"]))))(P(0, 0), 5)
            .withFill(COLOR).withStroke(9, ALMOST_TRANSPARENT);
        this.addCursor(this.cornerTopLeft, "nwse-resize");
        this.cornerTopRight = new (Interactable(Path.Circle, Adjustable(new EdgeResizeComponent(this, ["top", "right"]))))(P(0, 0), 5)
            .withFill(COLOR).withStroke(9, ALMOST_TRANSPARENT);
        this.addCursor(this.cornerTopRight, "nesw-resize");
        this.cornerBottomRight = new (Interactable(Path.Circle, Adjustable(new EdgeResizeComponent(this, ["right", "bottom"]))))(P(0, 0), 5)
            .withFill(COLOR).withStroke(9, ALMOST_TRANSPARENT);
        this.addCursor(this.cornerBottomRight, "nwse-resize");
        this.cornerBottomLeft = new (Interactable(Path.Circle, Adjustable(new EdgeResizeComponent(this, ["left", "bottom"]))))(P(0, 0), 5)
            .withFill(COLOR).withStroke(9, ALMOST_TRANSPARENT);
        this.addCursor(this.cornerBottomLeft, "nesw-resize");
        this.cornerTop = new (Interactable(Path.Circle, Adjustable(new EdgeResizeComponent(this, ["top"]))))(P(0, 0), 5)
            .withFill(COLOR).withStroke(9, ALMOST_TRANSPARENT);
        this.addCursor(this.cornerTop, "ns-resize");
        this.cornerRight = new (Interactable(Path.Circle, Adjustable(new EdgeResizeComponent(this, ["right"]))))(P(0, 0), 5)
            .withFill(COLOR).withStroke(9, ALMOST_TRANSPARENT);
        this.addCursor(this.cornerRight, "ew-resize");
        this.cornerBottom = new (Interactable(Path.Circle, Adjustable(new EdgeResizeComponent(this, ["bottom"]))))(P(0, 0), 5)
            .withFill(COLOR).withStroke(9, ALMOST_TRANSPARENT);
        this.addCursor(this.cornerBottom, "ns-resize");
        this.cornerLeft = new (Interactable(Path.Circle, Adjustable(new EdgeResizeComponent(this, ["left"]))))(P(0, 0), 5)
            .withFill(COLOR).withStroke(9, ALMOST_TRANSPARENT);
        this.addCursor(this.cornerLeft, "ew-resize");
        this.cornerHandles = [
            this.cornerBottomLeft,
            this.cornerBottomRight,
            this.cornerTopRight,
            this.cornerTopLeft,
            this.cornerLeft,
            this.cornerRight,
            this.cornerBottom,
            this.cornerTop,
        ];
        this.cornerHandles.forEach(i => i.applyMatrix = false);
        this.group.addChildren([
            this.movableRect,
            ...this.cornerHandles,
        ]);
        props.layer.addChild(this.group);
    }
    onStart() {
        this.text.visible = true;
    }
    onEnd() {
        this.text.visible = false;
    }
    addCursor(item, cursor) {
        item.onMouseEnter = () => this.props.setCursorHint(cursor);
        item.onMouseLeave = () => this.props.setCursorHint();
    }
    moveByDelta(delta) {
        this.moveAllAdjustableComponent.setDelta(delta);
    }
    componentWillUnmount() {
        this.group.remove();
    }
    componentDidUpdate(prevProps, prevState) {
        if (prevProps.left != this.props.left || prevProps.right != this.props.right || prevProps.top != this.props.top || prevProps.bottom != this.props.bottom) {
            this.setState({ left: this.props.left, bottom: this.props.bottom, right: this.props.right, top: this.props.top });
        }
    }
    setRect(path, bounds) {
        path.segments[0].point = bounds.topLeft;
        path.segments[1].point = bounds.topRight;
        path.segments[2].point = bounds.bottomRight;
        path.segments[3].point = bounds.bottomLeft;
    }
    render() {
        const bounds = RP(this.state.left.round(), this.state.top.round(), this.state.right.round(), this.state.bottom.round());
        this.setRect(this.movableRect, bounds);
        this.setRect(this.visibleRect, bounds);
        this.cornerTopLeft.position = bounds.topLeft;
        this.cornerBottomLeft.position = bounds.bottomLeft;
        this.cornerBottomRight.position = bounds.bottomRight;
        this.cornerTopRight.position = bounds.topRight;
        this.cornerTop.position = bounds.topCenter;
        this.cornerLeft.position = bounds.leftCenter;
        this.cornerRight.position = bounds.rightCenter;
        this.cornerBottom.position = bounds.bottomCenter;
        this.text.pivot = P(0, 10 / this.props.zoom);
        this.text.content = `${bounds.width} × ${bounds.height}`;
        this.text.position = bounds.topLeft;
        this.text.fontSize = FONT_SIZE / this.props.zoom;
        // Set interactivity and size appropriately for zoom level.
        this.cornerHandles.forEach(h => {
            h.visible = this.props.interactable;
            h.scaling = P(1 / this.props.zoom, 1 / this.props.zoom);
        });
        this.movableRect.strokeWidth = HITTABLE_STROKE_WIDTH / this.props.zoom;
        this.visibleRect.strokeWidth = VISIBLE_STROKE_WIDTH / this.props.zoom;
        return null;
    }
}
class EdgeResizeComponent {
    constructor(renderAreaBox, edges) {
        this.renderAreaBox = renderAreaBox;
        this.modifyEdge = { bottom: false, left: false, right: false, top: false };
        for (const edge of edges) {
            this.modifyEdge[edge] = true;
        }
    }
    onStart() {
        if ("onStart" in this.renderAreaBox)
            this.renderAreaBox.onStart();
        return {};
    }
    setDelta(delta) {
        const props = this.renderAreaBox.props;
        // Adjust delta if shift is held.
        if (Key.modifiers.shift) {
            const snapStep = stepLeq(this.renderAreaBox.props.zoom, [[0.33, 100], [0.75, 50], [1.5, 20]], 10);
            if (this.modifyEdge.left != this.modifyEdge.right) {
                // Adjust delta to snap for x.
                let width = props.right - props.left;
                delta.x = (delta.x - (delta.x % snapStep)) - (width % snapStep);
            }
            if (this.modifyEdge.top != this.modifyEdge.bottom) {
                // Adjust delta to snap for x.
                let height = props.bottom - props.top;
                delta.y = (delta.y - (delta.y % snapStep)) - (height % snapStep);
            }
        }
        this.renderAreaBox.setState({
            left: !this.modifyEdge.left ? props.left : this.modifyEdge.right ? props.left + delta.x : Math.min(props.left + delta.x, props.right - MIN_EDGE_SIZE),
            right: !this.modifyEdge.right ? props.right : this.modifyEdge.left ? props.right + delta.x : Math.max(props.right + delta.x, props.left + MIN_EDGE_SIZE),
            top: !this.modifyEdge.top ? props.top : this.modifyEdge.bottom ? props.top + delta.y : Math.min(props.top + delta.y, props.bottom - MIN_EDGE_SIZE),
            bottom: !this.modifyEdge.bottom ? props.bottom : this.modifyEdge.top ? props.bottom + delta.y : Math.max(props.bottom + delta.y, props.top + MIN_EDGE_SIZE),
        });
    }
    onEnd(delta) {
        this.renderAreaBox.onEnd();
        return new UpdateRenderAreaBox(this.renderAreaBox.state);
    }
    onNoop() {
        this.renderAreaBox.onEnd();
        return undefined;
    }
}
export class UpdateRenderAreaBox {
    constructor(value) {
        const current = appStore.getState().renderArea;
        this.previous = current ? Object.assign({}, current) : undefined;
        if (value) {
            if ("width" in value) {
                value = value.expand(Math.max(0, MIN_EDGE_SIZE - value.width), Math.max(0, MIN_EDGE_SIZE - value.height));
                // It's a paper.Rectangle
                this.value = { left: value.left, right: value.right, bottom: value.bottom, top: value.top };
            }
            else {
                this.value = Object.assign({}, value);
            }
            // Round all the values.
            let key;
            for (key in this.value) {
                this.value[key] = this.value[key].round();
            }
        }
        else {
            this.value = undefined;
        }
    }
    execute() {
        store.dispatchFn("UpdateRenderAreaBox", store => mutate(store, this.value));
    }
    undo() {
        21;
        store.dispatchFn("UpdateRenderAreaBox", store => mutate(store, this.previous));
    }
}
function mutate(store, renderArea) {
    return Object.assign(Object.assign({}, store), { renderArea });
}
